import * as d3 from 'd3';

export type ZoomTransform = d3.ZoomBehavior<SVGSVGElement, any>['transform'];

export abstract class BaseChartController {
  protected readonly svg: d3.Selection<SVGSVGElement, unknown, null, unknown>;

  constructor(svg: SVGSVGElement) {
    this.svg = d3.select(svg).attr('class', '').attr('xmlns:xhtml', 'http://www.w3.org/1999/xhtml');
  }

  /** Returns the SVG unit which corresponds to 1 pixel on the current screen. */
  protected getPixelUnit() {
    const node = this.svg.node()!;
    const pixelWidth = node.getBoundingClientRect().width;
    const viewBoxWidth = node.viewBox.baseVal.width;
    const result = viewBoxWidth / pixelWidth;
    if (isNaN(result) || result === Number.POSITIVE_INFINITY || result === Number.NEGATIVE_INFINITY) return 1;
    return result;
  }
}

export abstract class ZoomableChartController extends BaseChartController {
  protected isZoomActive: boolean = false;

  constructor(svg: SVGSVGElement) {
    super(svg);
    this.svg.call(this.zoom);
  }

  protected abstract performZoom(e: d3.D3ZoomEvent<SVGElement, any>): void | Promise<void>;

  protected abstract performZoomReset(transform: ZoomTransform, instant?: boolean): void | Promise<void>;

  public resetZoom(instant?: boolean) {
    this.isZoomActive = false;
    this.svg.classed('zoom-active', () => false);
    return this.performZoomReset(this.zoom.transform, instant);
  }

  private handleZoom(e: d3.D3ZoomEvent<SVGElement, any>) {
    // If the user double clicked, reset the zoom state
    if (e.sourceEvent?.type === 'dblclick') {
      if (!e.sourceEvent.cancelBubble) {
        e.sourceEvent.stopImmediatePropagation();
        this.resetZoom();
      }
      return;
    }
    return this.performZoom(e);
  }

  private handleZoomStart(e: d3.D3ZoomEvent<SVGElement, any>) {
    if (e.sourceEvent === null) return; // If we are explicitly calling this.zoom.transform, don't set the zoom state to active
    this.isZoomActive = true;
    this.svg.classed('zoom-active', () => true);
  }

  private handleZoomEnd(e: d3.D3ZoomEvent<SVGElement, any>) {
    // If the user zoomed all the way out, reset the zoom state
    const minZoom = this.zoom.scaleExtent()[0];
    if (e.transform.k === minZoom && this.isZoomActive) {
      this.resetZoom();
    }
  }

  private handleZoomFilter(e?: MouseEvent | TouchEvent) {
    const isMultiTouch = typeof (e as TouchEvent)?.touches?.length === 'number' && (e as TouchEvent).touches.length > 1;
    if (!!e?.ctrlKey || !!e?.metaKey || isMultiTouch) return true;
    return this.isZoomActive;
  }

  protected readonly zoom = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([1, 10])
    .filter(this.handleZoomFilter.bind(this))
    .on('zoom', this.handleZoom.bind(this))
    .on('start', this.handleZoomStart.bind(this))
    .on('end', this.handleZoomEnd.bind(this));
}

export abstract class BrushableChartController extends BaseChartController {
  protected brushSelection: [number, number] | undefined;
  private _isZoomActive: boolean = false;

  protected get isZoomActive() {
    return this._isZoomActive;
  }

  protected set isZoomActive(value: boolean) {
    this._isZoomActive = value;
    this.svg.classed('zoom-active', () => value);
  }

  constructor(svg: SVGSVGElement) {
    super(svg);
    this.svg.on('dblclick', this.performZoomReset.bind(this, false));
  }

  public resetZoom(instant?: boolean) {
    this.isZoomActive = false;
    this.brushSelection = undefined;
    this.performZoomReset(instant);
  }

  protected abstract performZoom(e: { selection: null | d3.D3BrushEvent<unknown>['selection'] }): void | Promise<void>;

  protected abstract performZoomReset(instant?: boolean): void | Promise<void>;

  private handleBrushEnd(e: d3.D3BrushEvent<unknown>) {
    if (e.selection) {
      this.isZoomActive = true;
      this.brushSelection = e.selection as [number, number];
    }
    this.performZoom(e);
  }

  protected readonly brush = d3.brushX().on('end', this.handleBrushEnd.bind(this));
}
